import * as React from 'react';
import dayjs from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { createTheme, ThemeProvider } from '@mui/material/styles';

dayjs.extend(isBetweenPlugin);

const calendarTheme = createTheme({
  palette: {
    primary: {
      main: '#F96343',
    },
    secondary: {
      main: '#ffd7be',
    },
  },
  components: {
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#F96343',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#F96343',
            },
          },
        },
      },
    },
  },
});

// const CustomPickersDay = styled(PickersDay, {
const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => 
    prop !== 'isSelected' && 
    prop !== 'isHovered' && 
    prop !== 'hasSession' &&
    prop !== 'isDisabled'
})(({ theme, isSelected, isHovered, hasSession,isDisabled, day }) => ({
  position: 'relative',
  borderRadius: 0,
  fontSize: '14px',
  color: '#666',
  
  // Session date styling (light pink circle)
  ...(hasSession && {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '25px',
      height: '25px',
      backgroundColor: '#FCEBE8', 
      color:'#F96343',
      borderRadius: '50%',
      zIndex: -1,
    }
  }),
  ...(isDisabled && {
    opacity: 0.5,
    pointerEvents: 'none',
    '&:hover::after': {
      content: '"🚫"',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: '20px',
    }
  }),
   ...(!isDisabled && {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#FCEBE8',
    }
  }),

  // Week selection styling (orange background with rounded edges)
  ...(isSelected && {
    backgroundColor: '#FF6347 !important', 
    color: '#fff !important',
    '&:hover': {
      backgroundColor: '#FF6347',
    },
    '&::after': {
      display: 'none',
    }
  }),

  // First day of selected week
  ...(isSelected && day?.day() === 0 && {
    borderTopLeftRadius: '20px',
    borderBottomLeftRadius: '20px',
  }),

  // Last day of selected week
  ...(isSelected && day?.day() === 6 && {
    borderTopRightRadius: '20px',
    borderBottomRightRadius: '20px',
  }),

  // Non-selected date styling
  '&:not(.Mui-selected)': {
    border: 'none',
    backgroundColor: 'transparent',
  },

  // Hover styling
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));
const isInSameWeek = (dayA, dayB) => {
  if (dayB == null) {
    return false;
  }
  return dayA.isSame(dayB, 'week');
};

function ServerDay(props) {
  const { 
    day, 
    selectedDay, 
    hoveredDay, 
    sessionDates, 
    startDate,
    endDate,
    ...other 
  } = props;

  const hasSession = sessionDates.some(date => 
    dayjs(date.startdate).format('YYYY-MM-DD') === day.format('YYYY-MM-DD')
  );

  const isDisabled = startDate && endDate ? (
    day.isBefore(dayjs(startDate), 'day') || day.isAfter(dayjs(endDate), 'day')
  ) : false;

  // Allow selection for dates between start and end dates
  const isSelectable = startDate && endDate ? (
    day.isAfter(dayjs(startDate), 'day') || day.isSame(dayjs(startDate), 'day') &&
    (day.isBefore(dayjs(endDate), 'day') || day.isSame(dayjs(endDate), 'day'))
  ) : true;

  return (
    <CustomPickersDay
      {...other}
      day={day}
      sx={{ px: 2.5 }}
      disableMargin
      selected={false}
      isSelected={isInSameWeek(day, selectedDay)}
      isHovered={isInSameWeek(day, hoveredDay)}
      hasSession={hasSession}
      isDisabled={isDisabled}
      disabled={isDisabled}
      onClick={isSelectable ? other.onClick : undefined}  
    />
  );
}

export default function WeekPicker({ 
  month, 
  setMonth, 
  selectedWeek, 
  setSelectedWeek,
  sessionDates = [] 
}) {
  const [hoveredDay, setHoveredDay] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

 const startDate = sessionDates.length > 0 
    ? dayjs(sessionDates[0].startdate)
    : null;
  const endDate = sessionDates.length > 0 
    ? dayjs(sessionDates[sessionDates.length - 1].startdate)
    : null;

  // const handleMonthChange = (newValue) => {
  //   setSelectedWeek(newValue);
  //   setMonth(newValue.format('MMMM'));
  //   setOpen(false);
  // };
    const handleMonthChange = (newValue) => {
    if (newValue && startDate && endDate) {
      if (
        (newValue.isAfter(startDate, 'day') || newValue.isSame(startDate, 'day')) &&
        (newValue.isBefore(endDate, 'day') || newValue.isSame(endDate, 'day'))
      ) {
        setSelectedWeek(newValue);
        setMonth(newValue.format('MMMM'));
        setOpen(false);
      }
    }
  };

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);
  
  return (
    <div ref={dropdownRef}>
      <button 
        className="month-dropdown-button" 
        onClick={() => setOpen(!open)} 
        style={{fontSize:'16px', fontWeight:'400', color:'#667085'}}
      >
        {month} 
        <span className="caret">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 6L8 10L12 6" stroke="#667085" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>
      {open && (
        <div className="month-dropdown-menu" style={{
          position: 'absolute',
          top: '40px',
          padding: '0px',
          width: 'fit-content',
          zIndex: 10,
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <ThemeProvider theme={calendarTheme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar 
                value={selectedWeek}
                onChange={handleMonthChange}
                showDaysOutsideCurrentMonth
                slots={{ day: ServerDay }}
                slotProps={{
                  day: (ownerState) => ({
                    selectedDay: selectedWeek,
                    hoveredDay,
                    sessionDates: sessionDates,
                     startDate: startDate,
                     endDate: endDate,
                    onPointerEnter: () => setHoveredDay(ownerState.day),
                    onPointerLeave: () => setHoveredDay(null),
                  }),
                }}
                sx={{
                  '& .MuiPickersYear-yearButton.Mui-selected': {
                    backgroundColor: '#F96343',
                    color: '#fff',
                  },
                  '& .MuiPickersYear-yearButton:hover': {
                    backgroundColor: '#ffd7be',
                  },
                  '& .MuiPickersMonth-monthButton.Mui-selected': {
                    backgroundColor: '#F96343',
                    color: '#fff',
                  },
                  '& .MuiPickersMonth-monthButton:hover': {
                    backgroundColor: '#ffd7be',
                  },
                  '& .MuiPickersArrowSwitcher-button': {
                    color: '#667085',
                  },
                  '& .MuiPickersCalendarHeader-label': {
                    color: '#667085',
                  }
                }}
              />
            </LocalizationProvider>
          </ThemeProvider>
        </div>
      )}
    </div>
  );
}